import { noop } from 'lodash';
import { httpCreators, useLoadingSelector } from '@ppvrbh/merchant-frontend-core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generalCreators } from '../../../redux/general/general-action';
import {
	DEFAULT_PAGE_SIZE,
	MODAL_TYPE,
} from '../../../redux/general/general-constant';
import { GENERAL_STATE_KEY } from '../../../redux/general/general-reducer';
import { RBHBox, RBHError, RBHText } from '../../../shared/components';
import styleText from '../../../shared/components/rbh-text/styles';
import { mainCreators } from '../../main-action';
import { MAIN_STATE_KEY } from '../../main-reducer';
import stylesCat from '../FoodCategory/styles';
import { FormAddEdit } from './components/FormAddEdit';
import { catCreators } from './redux/cat-action';
import { CAT_REQUEST_ID_LIST } from './redux/cat-constant';
import { CAT_STATE_KEY, catStateSelector } from './redux/cat-reducer';
import { catSagas } from './redux/cat-saga';
import { CatService } from './redux/cat-service';
import styles from './styles';
import './stylescss.css';
import RBHTable2 from '../../../shared/components/rbh-table-2';
import { TabHeader } from './components/CategoryTab/TabHeader/TabHeader';
import { AlertBar } from './components/CategoryTab/AlertBar/AlertBar';
import { Header } from './components/CategoryTab/Header';
import { StarFilled } from '@ant-design/icons';
import { DEBOUNCE_TIMEOUT } from '../../../shared/helpers/constant';
import { useHistory } from 'react-router';
import { FormattedMessage } from 'react-intl';

interface MenuCategoryData {
	menuCategoryId: number;
	menuCategoryName: string;
	menuCategoryNameEN: string | null;
	menuCategoryTypeId: number;
	menuCount?: number;
}

export const FoodCategoryScreen: React.FC<Props> = () => {
	const dispatch = useDispatch();
	const data = useSelector(catStateSelector(CAT_STATE_KEY.LIST)) as MenuCategoryData[];
	const loading = useLoadingSelector(CAT_REQUEST_ID_LIST.GET_CAT);
	const [edit, setEdit] = useState(null);
	const [search, setSearch] = useState<string | null>(null);
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);

	useEffect(() => {
		dispatch(mainCreators.setMainState(MAIN_STATE_KEY.HEADER_MENU, 4));

		resetReload();
	}, []);

	const pushToCat = () => {
		resetReload();
	};

	const resetReload = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
		setEdit(null);
		dispatch(
			catCreators.callFoodCategory(noop, true, noop, {
				menuCategoryCount: true,
				menuCount: true,
				keyword: search ?? '',
				page,
				limit,
			}),
		);
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	const handleDeleteItem = (id: number) => {
		dispatch(
			httpCreators.request({
				id: CAT_REQUEST_ID_LIST.DELETE_CAT,
				sagaHandler: catSagas.deleteCatHandler as any,
				callbackParams: resetReload,
				service: CatService.deleteCat,
				serviceParams: id,
			}),
		);
	};

	const handlePageChange = (current?: number, limit?: number) => {
		if (current) {
			setPage(current);
		}
		if (limit) {
			setLimit(limit);
		}
	};

	const handleSortedSuccess = () => {
		dispatch(
			catCreators.setCatState(CAT_STATE_KEY.ALERT_SORT_SUCCESS, {
				isVisible: true,
			}),
		);

		dispatch(
			catCreators.setCatState(CAT_STATE_KEY.FULL_LIST, undefined),
		);
		resetReload();
	};

	useEffect(() => {
		if (search === null) {
			return;
		}
		const timeout = setTimeout(() => {
			resetReload();
		}, DEBOUNCE_TIMEOUT);

		return () => clearTimeout(timeout);
	}, [search]);

	useEffect(() => {
		resetReload();
	}, [page, limit]);

	if (loading) {
		return null;
	}

	return (
		<RBHBox>
			{edit ? (
				<>
					<AlertBar />
					<EditComponent
						data={edit}
						onOk={pushToCat}
						onSuccess={pushToCat}
						onNotificationError={pushToCat}
					/>
				</>
			) : (
				<RBHBox>
					<Header />
					<AlertBar />
					<TabHeader
						search={search}
						onSearchChange={handleSearchChange}
						onSortedSuccess={handleSortedSuccess}
					/>
					<RBHBox>
						<DisplayContent
							data={data}
							onClickEdit={data => setEdit(data as any)}
							onEditSuccess={resetReload}
							onDelete={handleDeleteItem}
							onPageChange={handlePageChange}
						/>
					</RBHBox>
				</RBHBox>
			)}
		</RBHBox>
	);
};

interface DisplayContentProps {
	data: MenuCategoryData[];
	onPageChange: (current?: number, limit?: number) => void;
	onClickEdit: (data: any) => void;
	onEditSuccess: () => void;
	onDelete: (id: number) => void;
}
const DisplayContent = ({
	data,
	onClickEdit,
	onDelete,
	onPageChange,
}: DisplayContentProps) => {
	const history = useHistory();
	const pageMeta: any = useSelector(
		catStateSelector(CAT_STATE_KEY.LIST_META_DATA),
	);
	const dispatch = useDispatch();

	const onClickRow = (foodCategoryId: number) => {
		history.push(`/main/foodcategory/${foodCategoryId}`);
	};

	const onEdit = (foodCategoryId: number) => {
		history.push(`/main/foodcategory/${foodCategoryId}`);
	};

	return (
		<RBHBox style={stylesCat.defaultContent}>
			<RBHTable2
				style={styles.table}
				onRow={row => ({
					onClick: () => onClickRow(row.id)
				})}
				rowClassName={'pointer'}
				columns={[
					{
						title: <FormattedMessage id='main.cat_category.table.title.name_th'/>,
						dataIndex: 'menuCategoryName',
						render: (_, record) => {
							const isPromotionCategory =
								record.menuCategoryName === 'โปรโมชัน';
							if (!isPromotionCategory) return record.menuCategoryName;
							return (
								<RBHBox style={styles.gap8}>
									<StarFilled
										width={20}
										style={styles.starIcon}
										translate={null}
									/>
									<span>{record.menuCategoryName}</span>
								</RBHBox>
							);
						},
					},
					{ title: <FormattedMessage id='main.cat_category.table.title.name_en'/>, dataIndex: 'menuCategoryNameEN' },
					{ title: <FormattedMessage id='main.cat_category.table.title.menu_count'/>, dataIndex: 'itemsAmount' },
				]}
				actions={{
					onEdit: (item) => onEdit(item.id),
					onDelete: (value) =>
						dispatch(
							generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
								visible: true,
								onOk: () => onDelete(value.id),
								modalTitle: 'main.cat_category_delete_header',
								modalContent: 'main.cat_category_delete_content',
								type: MODAL_TYPE.DELETE,
							}),
						),
				}}
				onChange={(value) => onPageChange(value.current, value.pageSize)}
				dataSource={data.map((item) => {
					const isPromotionCategory = item.menuCategoryName === 'โปรโมชัน';

					return {
						id: item.menuCategoryId,
						menuCategoryName: item.menuCategoryName,
						menuCategoryNameEN: item.menuCategoryNameEN,
						itemsAmount: item.menuCount ?? 0,
						showEditIcon: !isPromotionCategory,
						showDeleteIcon: false,
					};
				})}
				pagination={{
					showSizeChanger: true,
					pageSize: pageMeta?.itemsPerPage,
					current: pageMeta?.currentPage,
					total: pageMeta?.totalItems,
				}}
			/>
		</RBHBox>
	);
};

interface EditComponentProps {
	data: any;
	onOk: () => void;
	onSuccess: () => void;
	onNotificationError: () => void;
}
const EditComponent = ({
	data,
	onSuccess,
	onOk,
	onNotificationError,
}: EditComponentProps) => {
	const dispatch = useDispatch();

	return (
		<RBHBox>
			<RBHBox style={stylesCat.header}>
				<RBHText
					style={styleText.headerText}
					text={'main.cat_category_edit'}
					data-testid={'cat_category_edit'}
					id={'cat_category_edit'}
				/>
			</RBHBox>
			<RBHBox style={stylesCat.defaultContent}>
				<FormAddEdit
					editData={data}
					onSuccessCallback={onSuccess}
					onCancel={() =>
						dispatch(
							generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
								visible: true,
								onOk: onOk,
								type: MODAL_TYPE.CONFIRM_EDIT,
							}),
						)
					}
					onNotificationError={onNotificationError}
				/>
			</RBHBox>
		</RBHBox>
	);
};

type Props = {};
